import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { coursesApi } from '@/api/courses';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select, type SelectOption } from '@/components/ui/Select';
import {
  lessonOptions,
  moduleOptions,
  moveLock,
  sectionOptions,
  transferErrorText,
  type TransferKind,
  type TransferOp,
} from '@/lib/contentTransfer';

/**
 * «Копировать в…» и «Переместить в…» для блока, раздела и урока
 * (договор `docs/CONTRACTS/constructor-v2.md` §2).
 *
 * Место выбирается по цепочке: курс → урок → раздел для блока, курс → урок для
 * раздела, курс → модуль для урока. Узел встаёт В КОНЕЦ выбранного места — так
 * решил сервер, и окно об этом говорит, чтобы методист не искал его наверху.
 *
 * ⚠️ ОБРАЗЦЫ В СПИСКЕ КУРСОВ НЕ ПОКАЗЫВАЮТСЯ. Класть в образец сервер не даёт
 * (423), и предлагать место, куда нельзя, — значит заставить человека узнать это
 * отказом.
 */

export type TransferSource = {
  /** Курс, где лежит переносимое, — отсюда выбор начинается и отсюда проверяется подтип. */
  marathonId?: string;
  lessonId?: string;
  sectionId?: string;
  isTemplate?: boolean;
};

export type TransferResult = { op: TransferOp; kind: TransferKind; targetLessonId?: string };

const TITLE_KEYS: Record<TransferKind, Record<TransferOp, string>> = {
  block: { copy: 'lessonBuilder.copyBlockTitle', move: 'lessonBuilder.moveBlockTitle' },
  section: { copy: 'lessonBuilder.copySectionTitle', move: 'lessonBuilder.moveSectionTitle' },
  lesson: { copy: 'lessonBuilder.copyLessonTitle', move: 'lessonBuilder.moveLessonTitle' },
};

/**
 * Шесть вызовов развёрнуты руками, а не собраны из `kind` и `op`: пути в
 * `coursesApi` обязаны быть буквальными (см. там же).
 */
function runTransfer(kind: TransferKind, op: TransferOp, id: string, targetId: string): Promise<{ id: string }> {
  if (kind === 'block') return op === 'copy' ? coursesApi.copyBlock(id, targetId) : coursesApi.moveBlock(id, targetId);
  if (kind === 'section') return op === 'copy' ? coursesApi.copySection(id, targetId) : coursesApi.moveSection(id, targetId);
  return op === 'copy' ? coursesApi.copyLesson(id, targetId) : coursesApi.moveLesson(id, targetId);
}

export function TransferDialog({
  kind,
  op,
  sourceId,
  source,
  beforeRun,
  onDone,
  onClose,
}: {
  kind: TransferKind;
  op: TransferOp;
  /** Серверный id переносимого. */
  sourceId: string;
  source: TransferSource;
  /**
   * Что сделать перед запросом. Конструктор здесь сохраняет урок: перенесётся то,
   * что лежит на сервере, а не то, что на экране. Вернуть текст — перенос отменён.
   */
  beforeRun?: () => Promise<string | null>;
  onDone?: (r: TransferResult) => void | Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(h);
  }, [query]);

  // Из образца начинать выбор нельзя: он не может быть местом назначения.
  const [marathonId, setMarathonId] = useState(source.isTemplate ? '' : (source.marathonId ?? ''));
  const [lessonId, setLessonId] = useState(kind === 'lesson' || source.isTemplate ? '' : (source.lessonId ?? ''));
  const [sectionId, setSectionId] = useState(kind === 'block' && !source.isTemplate ? (source.sectionId ?? '') : '');
  const [moduleId, setModuleId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ lessonId?: string } | null>(null);

  const coursesQ = useQuery({
    queryKey: ['admin', 'transfer', 'marathons', q],
    queryFn: () => coursesApi.listAllMarathons({ template: 'false', q: q || undefined, limit: 100 }),
  });
  const courseQ = useQuery({
    queryKey: ['admin', 'transfer', 'marathon', marathonId],
    queryFn: () => coursesApi.getMarathon(marathonId),
    enabled: !!marathonId,
  });
  const lessonQ = useQuery({
    queryKey: ['admin', 'transfer', 'lesson', lessonId],
    queryFn: () => coursesApi.getFullLesson(lessonId),
    enabled: kind === 'block' && !!lessonId,
  });
  /**
   * Подтип курса-источника. Перемещать из курса с подтипом сервер не даст
   * (`409 move_from_subtype`) — говорим об этом до выбора места, а не после.
   * Не ответил — не запираем: сервер откажет сам, и текст отказа разобран.
   */
  const settingsQ = useQuery({
    queryKey: ['admin', 'transfer', 'source-settings', source.marathonId],
    queryFn: () => coursesApi.getMarathonSettings(source.marathonId!),
    enabled: op === 'move' && !!source.marathonId && !source.isTemplate,
  });

  const courseOptions: SelectOption[] = (coursesQ.data?.items ?? []).map((c) => ({ value: c.id, label: c.title }));
  // Выбранный курс мог не попасть в первую сотню или в поиск — держим его в списке.
  if (marathonId && courseQ.data && !courseOptions.some((o) => o.value === marathonId)) {
    courseOptions.unshift({ value: marathonId, label: courseQ.data.title });
  }

  const lessons = lessonOptions(courseQ.data);
  const modules = moduleOptions(courseQ.data);
  const sections = sectionOptions(lessonQ.data?.sections, t('lessonBuilder.transferHomeworkMark'));

  const lessonValue = lessons.some((o) => o.value === lessonId) ? lessonId : '';
  const sectionValue = sections.some((o) => o.value === sectionId) ? sectionId : '';
  // Модуль один — он и есть место; спрашивать о нём не нужно.
  const moduleValue = modules.length === 1 ? modules[0].value : modules.some((o) => o.value === moduleId) ? moduleId : '';

  const targetId = kind === 'block' ? sectionValue : kind === 'section' ? lessonValue : moduleValue;
  const lock = op === 'move' ? moveLock({ isTemplate: source.isTemplate, subtype: settingsQ.data?.subtype }) : null;
  const canRun = !!targetId && !lock && !busy && !(op === 'move' && settingsQ.isLoading);

  const pickCourse = (id: string) => {
    setMarathonId(id);
    setLessonId('');
    setSectionId('');
    setModuleId('');
    setError(null);
  };

  const run = async () => {
    if (!canRun) return;
    setBusy(true);
    setError(null);
    try {
      if (beforeRun) {
        const reason = await beforeRun();
        if (reason) {
          setError(reason);
          return;
        }
      }
      const res = await runTransfer(kind, op, sourceId, targetId);
      setDone({ lessonId: kind === 'lesson' ? res.id : lessonValue });
      await onDone?.({ op, kind, targetLessonId: kind === 'lesson' ? undefined : lessonValue });
    } catch (e) {
      setError(transferErrorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (!busy) onClose();
  };

  return (
    // phoneFull: на телефоне окно во весь экран (карточка курса, шаг 7 кураторки и админки на телефоне, §5.3) — четыре
    // выбора столбиком в маленьком окне листались внутри него. Кнопки остаются под полями: файл в папке конструктора.
    <Modal open onClose={close} title={t(TITLE_KEYS[kind][op])} phoneFull>
      {done ? (
        <div className="space-y-4">
          <p className="flex items-center gap-2 text-sm text-on-surface">
            <span className="material-symbols-outlined text-success">check_circle</span>
            {t(op === 'copy' ? 'lessonBuilder.transferDoneCopy' : 'lessonBuilder.transferDoneMove')}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            {done.lessonId && (
              <Link to={`/admin/lessons/${done.lessonId}/builder`} onClick={onClose}>
                <Button variant="secondary">{t('lessonBuilder.transferOpenLesson')}</Button>
              </Link>
            )}
            <Button onClick={onClose}>{t('lessonBuilder.transferClose')}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {lock && (
            <p className="rounded-button bg-tertiary-container px-3 py-2 text-sm text-on-tertiary-container">
              {lock === 'template'
                ? t('lessonBuilder.transferMoveLockTemplate')
                : t('lessonBuilder.transferMoveLockSubtype', { subtype: settingsQ.data?.subtype ?? '' })}
            </p>
          )}

          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('lessonBuilder.transferCourseSearch')}
            leftIcon={<span className="material-symbols-outlined">search</span>}
          />
          <Select
            label={t('lessonBuilder.transferCourse')}
            placeholder={t('lessonBuilder.transferPick')}
            value={marathonId}
            options={courseOptions}
            onChange={(e) => pickCourse(e.target.value)}
          />

          {marathonId && courseQ.data && kind === 'lesson' && modules.length === 0 && (
            <p className="text-sm text-on-surface-variant">{t('lessonBuilder.transferNoModules')}</p>
          )}
          {kind === 'lesson' && modules.length > 1 && (
            <Select
              label={t('lessonBuilder.transferModule')}
              placeholder={t('lessonBuilder.transferPick')}
              value={moduleValue}
              options={modules}
              onChange={(e) => setModuleId(e.target.value)}
            />
          )}

          {kind !== 'lesson' && marathonId && courseQ.data && lessons.length === 0 && (
            <p className="text-sm text-on-surface-variant">{t('lessonBuilder.transferNoLessons')}</p>
          )}
          {kind !== 'lesson' && lessons.length > 0 && (
            <Select
              label={t('lessonBuilder.transferLesson')}
              placeholder={t('lessonBuilder.transferPick')}
              value={lessonValue}
              options={lessons}
              onChange={(e) => {
                setLessonId(e.target.value);
                setSectionId('');
                setError(null);
              }}
            />
          )}

          {kind === 'block' && lessonValue && lessonQ.data && sections.length === 0 && (
            <p className="text-sm text-on-surface-variant">{t('lessonBuilder.transferNoSections')}</p>
          )}
          {kind === 'block' && sections.length > 0 && (
            <Select
              label={t('lessonBuilder.transferSection')}
              placeholder={t('lessonBuilder.transferPick')}
              value={sectionValue}
              options={sections}
              onChange={(e) => {
                setSectionId(e.target.value);
                setError(null);
              }}
            />
          )}

          <ul className="space-y-1 text-xs text-on-surface-variant">
            <li>{t('lessonBuilder.transferToEnd')}</li>
            {op === 'copy' && kind === 'lesson' && <li>{t('lessonBuilder.transferCopyLessonDraft')}</li>}
            {op === 'copy' && kind === 'section' && <li>{t('lessonBuilder.transferCopySectionDeadline')}</li>}
          </ul>

          {(error || coursesQ.error) && (
            <p role="alert" className="rounded-button bg-error-container px-3 py-2 text-sm text-on-error-container">
              {error ?? transferErrorText(coursesQ.error, t)}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={close} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button onClick={run} loading={busy} disabled={!canRun}>
              <span className="material-symbols-outlined">{op === 'copy' ? 'content_copy' : 'arrow_forward'}</span>
              {t(TITLE_KEYS[kind][op])}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Кнопка «⋯» с двумя пунктами. Пока узла нет на сервере, пункты неактивны и
 * названа причина — иначе окно откроется и откажет уже после выбора места.
 */
export function TransferMenu({
  onPick,
  disabledReason,
}: {
  onPick: (op: TransferOp) => void;
  disabledReason?: string | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={t('lessonBuilder.transferMenu')}
        aria-label={t('lessonBuilder.transferMenu')}
        aria-expanded={open}
        className="rounded-button p-1 text-on-surface-variant transition hover:bg-surface-container-high"
      >
        <span className="material-symbols-outlined text-base">more_vert</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          {/* w-48, а не шире: на 390 кнопка стоит в середине строки урока, и меню
              шириной 240 px вылезало за левый край экрана вместе со значками. */}
          <div className="absolute right-0 z-30 mt-1 w-48 rounded-card bg-surface-container-lowest p-1 shadow-ambient">
            {disabledReason && <p className="px-3 py-2 text-xs text-on-surface-variant">{disabledReason}</p>}
            {(['copy', 'move'] as const).map((op) => (
              <button
                key={op}
                type="button"
                disabled={!!disabledReason}
                onClick={() => {
                  setOpen(false);
                  onPick(op);
                }}
                className="flex w-full items-center gap-2 rounded-button px-3 py-2 text-left text-sm text-on-surface hover:bg-surface-container-high disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <span className="material-symbols-outlined text-base">{op === 'copy' ? 'content_copy' : 'arrow_forward'}</span>
                {t(op === 'copy' ? 'lessonBuilder.copyTo' : 'lessonBuilder.moveTo')}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
